import { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowsLeftRightIcon, FilePdfIcon } from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { ConfirmScheduleChangeModal } from '../components/modals/ConfirmScheduleChangeModal';
import { cn } from '../lib/utils';

function apiErrorCode(error) {
  const d = error?.response?.data;
  return d?.errorCode || d?.code || d?.error?.code || d?.error?.errorCode;
}

/** GET /api/packs/:id/switch — `data` is pack[]; `description` may be top-level or on `data` if shaped as an object. */
const sectionWrapClass = 'w-full';

function schedulePdfUrl(pack) {
  const u = pack?.schedulePdf;
  return typeof u === 'string' && u.trim() !== '' ? u.trim() : '';
}

function parseSwitchPackGetResponse(res) {
  const body = res?.data ?? {};
  const rawData = body.data;
  let list = [];
  if (Array.isArray(rawData)) {
    list = rawData;
  } else if (rawData && typeof rawData === 'object' && Array.isArray(rawData.packs)) {
    list = rawData.packs;
  }
  return { list };
}

export function PackSwitchSection({ originalPackId, currentPack, enabled, refetchPacks }) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [subtitle, setSubtitle] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!enabled || !originalPackId || !currentPack?._id) {
      setOptions([]);
      setSelectedId(null);
      setSubtitle('');
      setDismissed(false);
      setConfirmOpen(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setDismissed(false);
      setConfirmOpen(false);
      setOptions([]);
      setSelectedId(null);
      setSubtitle('');
      try {
        const res = await axios.get(`/api/packs/${originalPackId}/switch`);
        const { list } = parseSwitchPackGetResponse(res);
        if (cancelled) return;
        if (!list.length) {
          setDismissed(true);
          return;
        }
        const originId = String(originalPackId);
        const alternatives = list.filter((p) => String(p._id) !== originId);
        const currentRow = {
          _id: currentPack._id,
          title: currentPack.title,
          icon: currentPack.icon,
          schedulePdf: schedulePdfUrl(currentPack),
          scheduleMeta: currentPack.scheduleMeta,
        };
        setOptions([currentRow, ...alternatives]);
        setSelectedId(originId);
      } catch (err) {
        if (cancelled) return;
        const code = apiErrorCode(err);
        if (code === 'packSwitchNotAllowed') {
          setDismissed(true);
          return;
        }
        if (code === 'packSwitchAlreadyRequested') {
          setDismissed(true);
          return;
        }
        setDismissed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [enabled, originalPackId, currentPack?._id]);

  const postSwitch = async (body) => {
    setSubmitting(true);
    try {
      await axios.post(`/api/packs/${originalPackId}/switch`, body);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Something went wrong. Please try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const executeConfirm = async () => {
    if (!selectedId) {
      toast.error('Choose a test series.');
      setConfirmOpen(false);
      return;
    }
    const keepingCurrent = String(selectedId) === String(originalPackId);
    if (keepingCurrent) {
      const ok = await postSwitch({ switchRejected: true });
      if (!ok) return;
      toast.success('You will keep your current test series.');
      setConfirmOpen(false);
      setDismissed(true);
      setOptions([]);
      setSelectedId(null);
      setSubtitle('');
      return;
    }
    const ok = await postSwitch({ switchedPackId: selectedId });
    if (!ok) return;
    toast.success('Pack switched successfully');
    try {
      await refetchPacks(selectedId);
      setConfirmOpen(false);
    } catch {
      toast.error('Pack was switched but the dashboard could not refresh. Please reload the page.');
      setConfirmOpen(false);
    }
  };

  if (!enabled || !originalPackId || dismissed) {
    return null;
  }

  if (!currentPack?._id) {
    return null;
  }

  if (loading) {
    return (
      <div className={cn(sectionWrapClass, 'max-md:-mx-4 max-md:w-[calc(100%+2rem)] md:mx-0 md:w-full')}>
        <Card className="w-full rounded-xl border border-[var(--q3-neutral-light-v3)]/20 bg-[var(--q3-surface-default)] shadow-sm max-md:rounded-t-2xl max-md:border-x-0">
          <CardContent className="py-4 text-center text-sm text-[var(--q3-neutral-light-v3)]">
            Checking switch options…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (options.length === 0) {
    return null;
  }

  const confirmMode = String(selectedId) === String(originalPackId) ? 'keep' : 'switch';
  const selectedOption = options.find((p) => String(p._id) === String(selectedId));

  return (
    <div className={cn(sectionWrapClass, 'max-md:-mx-4 max-md:w-[calc(100%+2rem)] md:mx-0 md:w-full')}>
      <ConfirmScheduleChangeModal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        mode={confirmMode}
        targetTitle={selectedOption?.scheduleMeta?.title || selectedOption?.title}
        isSubmitting={submitting}
        confirmLabel={'Confirm schedule'}
        onConfirm={executeConfirm}
      />
      <Card
        className={cn(
          'w-full rounded-xl border border-[var(--q3-neutral-light-v3)]/20 bg-[var(--q3-surface-default)] shadow-sm',
          'max-md:gap-4 max-md:rounded-none max-md:rounded-t-2xl max-md:border-x-0 max-md:border-y max-md:border-[var(--q3-neutral-light-v3)]/20 max-md:py-4 max-md:shadow-md',
        )}
      >
        <CardHeader className="max-md:px-3 max-md:pb-1 max-md:pt-0 items-start pb-2 text-left sm:px-6">
          <CardTitle className="flex w-full flex-col items-start gap-2 text-lg font-semibold text-[var(--q3-neutral-default)] sm:flex-row sm:items-center sm:gap-2">
            <ArrowsLeftRightIcon className="h-5 w-5 shrink-0 text-[var(--q3-accent-normal)]" />
            <span className="w-full text-left">Choose your test schedule</span>
          </CardTitle>
          <CardDescription className="w-full max-w-none text-left text-sm leading-relaxed text-[var(--q3-neutral-light-v3)]">
            {subtitle ||
              "Pick the schedule aligned with your coaching/preparation. All your tests will be scheduled accordingly. Choose carefully as this can't be changed later."}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 pt-0 sm:px-6">
          <RadioGroup
            value={selectedId != null ? String(selectedId) : ''}
            onValueChange={setSelectedId}
            disabled={submitting}
            className="flex w-full flex-col items-stretch gap-2"
          >
            {options.map((pack) => {
              const value = String(pack._id);
              const controlId = `pack-switch-${value}`;
              const isSelected = selectedId != null && String(selectedId) === value;
              const scheduleUrl = schedulePdfUrl(pack);
              return (
                <Label
                  key={pack._id}
                  htmlFor={controlId}
                  className={cn(
                    'flex w-full cursor-pointer flex-col items-start gap-3 rounded-xl border px-3 py-3 text-left font-normal transition-[border-color,box-shadow,background-color]',
                    'border-[var(--q3-neutral-light-v3)]/25 bg-[var(--q3-surface-default)]',
                    'focus-within:ring-2 focus-within:ring-[var(--q3-accent-normal)]/35 focus-within:outline-none',
                    'sm:flex-row sm:items-center sm:gap-3',
                    isSelected &&
                      'border-[var(--q3-accent-normal)] bg-[var(--q3-accent-light)]/12 shadow-[0_0_0_1px_var(--q3-accent-normal)]',
                    submitting && 'pointer-events-none opacity-60',
                  )}
                >
                  <div className="flex w-full min-w-0 flex-1 items-center justify-start gap-3 self-stretch">
                    <RadioGroupItem
                      value={value}
                      id={controlId}
                      color="var(--q3-accent-normal)"
                      className={cn(
                        'size-4 shrink-0 border-2 border-[var(--q3-neutral-light-v3)] shadow-none',
                        'bg-transparent text-[var(--q3-accent-normal)] dark:bg-transparent',
                        'data-[state=checked]:border-[var(--q3-accent-normal)]',
                        'focus-visible:ring-offset-0',
                      )}
                    />
                    <img
                      alt=""
                      src={pack.icon || '/placeholder.svg'}
                      className="h-8 w-8 md:h-9 md:w-9 shrink-0 rounded-md object-cover"
                    />
                    <span className="min-w-0 w-0 flex-1 space-y-1">
                      <span className="block w-full font-medium text-[var(--q3-neutral-default)]">{pack.title}</span>
                      {typeof pack?.scheduleMeta?.description === 'string' &&
                        pack?.scheduleMeta?.description.trim() !== '' && (
                          <span className="block w-full max-w-none text-sm leading-snug text-[var(--q3-neutral-light-v3)]">
                            {pack?.scheduleMeta?.description.trim()}
                          </span>
                        )}
                    </span>
                  </div>
                  {scheduleUrl ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full shrink-0 justify-center gap-2 whitespace-nowrap border-[var(--q3-stroke-normal)] text-[var(--q3-neutral-default)] hover:bg-[var(--q3-surface-dim)] sm:w-auto sm:justify-center"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(scheduleUrl, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      <FilePdfIcon /> View schedule
                    </Button>
                  ) : null}
                </Label>
              );
            })}
          </RadioGroup>
        </CardContent>
        <CardFooter className="flex flex-col items-stretch gap-2 border-t border-[var(--q3-neutral-light-v3)]/15 px-3 pt-4 sm:items-start sm:px-6">
          <Button
            type="button"
            disabled={submitting || !selectedId}
            onClick={() => setConfirmOpen(true)}
            className={cn(
              'w-full min-w-0 shrink-0 sm:w-auto sm:min-w-[11rem]',
              '!bg-[var(--q3-accent-normal)] !text-[var(--q3-neutral-default_inverted)] hover:!bg-[var(--q3-accent-dark)]',
            )}
          >
            {submitting
              ? 'Saving…'
              : `Continue with ${selectedOption?.scheduleMeta?.title || selectedOption?.title}'s Schedule`}
          </Button>
          <p className="mt-2 w-full max-w-none text-left text-sm leading-snug text-[var(--q3-danger-normal)]">
            NOTE: You can only change your schedule once, this is irreversible.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
